export type LogEventType = 'plain' | 'system' | 'tool_call' | 'tool_result' | 'text' | 'result'

export interface LogEvent {
  id: string
  type: LogEventType
  timestamp?: string
  data: Record<string, unknown>
}

interface PlainEvent extends LogEvent {
  type: 'plain'
  data: { text: string }
}

interface SystemEvent extends LogEvent {
  type: 'system'
  data: { model: string; tools: string[]; agents: string[] }
}

interface ToolCallEvent extends LogEvent {
  type: 'tool_call'
  data: { toolName: string; input: Record<string, unknown>; toolUseId: string; parentToolUseId?: string | null; durationMs?: number }
}

interface ToolResultEvent extends LogEvent {
  type: 'tool_result'
  data: { toolUseId: string; content: string; isError: boolean; parentToolUseId?: string | null }
}

interface TextEvent extends LogEvent {
  type: 'text'
  data: { text: string; parentToolUseId?: string | null }
}

interface ResultEvent extends LogEvent {
  type: 'result'
  data: { text: string; cost?: number; duration?: number }
}

export type ParsedLogEvent = PlainEvent | SystemEvent | ToolCallEvent | ToolResultEvent | TextEvent | ResultEvent

function stripTimestamp(line: string): { timestamp: string | undefined; content: string } {
  // Docker `-t` format: "2026-02-18T16:29:28.452345678Z content"
  const dockerMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z)\s(.*)$/)
  if (dockerMatch) {
    return { timestamp: dockerMatch[1], content: dockerMatch[2].trimStart() }
  }
  // Fallback: "2026-02-18 16:29:28.452 | content"
  const pipeMatch = line.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}\.\d+)\s*\|(.*)$/)
  if (pipeMatch) {
    return { timestamp: pipeMatch[1], content: pipeMatch[2].trimStart() }
  }
  return { timestamp: undefined, content: line }
}

function parseTimestamp(ts: string | undefined): number | null {
  if (!ts) return null
  // Docker format: "2026-02-18T16:29:28.452345678Z" — truncate nanoseconds to ms
  if (ts.includes('T')) {
    const truncated = ts.replace(/(\.\d{3})\d*Z$/, '$1Z')
    const d = new Date(truncated)
    return isNaN(d.getTime()) ? null : d.getTime()
  }
  // Fallback format: "2026-02-18 16:29:28.452"
  const d = new Date(ts.replace(' ', 'T') + 'Z')
  return isNaN(d.getTime()) ? null : d.getTime()
}

function tryParseJson(text: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(text)
    if (typeof parsed === 'object' && parsed !== null) return parsed as Record<string, unknown>
  } catch {
    // not JSON
  }
  return null
}

function getToolInputSummary(toolName: string, input: Record<string, unknown>): string {
  switch (toolName) {
    case 'Bash':
      return (input.command as string) || ''
    case 'Read':
      return (input.file_path as string) || ''
    case 'Write':
    case 'Edit':
      return (input.file_path as string) || ''
    case 'Glob':
      return (input.pattern as string) || ''
    case 'Grep':
      return `${input.pattern || ''} ${input.path ? `in ${input.path}` : ''}`.trim()
    case 'Task':
      return (input.description as string) || (input.prompt as string) || ''
    case 'WebFetch':
      return (input.url as string) || ''
    case 'WebSearch':
      return (input.query as string) || ''
    default:
      return JSON.stringify(input).slice(0, 200)
  }
}

export function parseSessionLogs(rawLogs: string): ParsedLogEvent[] {
  if (!rawLogs) return []

  const lines = rawLogs.split('\n').filter(l => l.trim())
  const events: ParsedLogEvent[] = []
  let eventIndex = 0

  for (const line of lines) {
    const { timestamp, content } = stripTimestamp(line)
    const json = tryParseJson(content)

    if (!json) {
      // Plain text line
      events.push({
        id: `evt-${eventIndex++}`,
        type: 'plain',
        timestamp,
        data: { text: content },
      })
      continue
    }

    const type = json.type as string

    // Skip stream_event deltas — we get the full assistant messages
    if (type === 'stream_event') continue

    if (type === 'system' && json.subtype === 'init') {
      events.push({
        id: `evt-${eventIndex++}`,
        type: 'system',
        timestamp,
        data: {
          model: (json.model as string) || 'unknown',
          tools: (json.tools as string[]) || [],
          agents: (json.agents as string[]) || [],
        },
      })
      continue
    }

    if (type === 'assistant') {
      const message = json.message as Record<string, unknown> | undefined
      if (!message) continue

      const content_blocks = message.content as Array<Record<string, unknown>> | undefined
      if (!content_blocks) continue

      const parentToolUseId = json.parent_tool_use_id as string | null

      for (const block of content_blocks) {
        if (block.type === 'tool_use') {
          events.push({
            id: `evt-${eventIndex++}`,
            type: 'tool_call',
            timestamp,
            data: {
              toolName: block.name as string,
              input: block.input as Record<string, unknown>,
              toolUseId: block.id as string,
              parentToolUseId,
            },
          })
        } else if (block.type === 'text') {
          const text = block.text as string
          if (text?.trim()) {
            events.push({
              id: `evt-${eventIndex++}`,
              type: 'text',
              timestamp,
              data: { text, parentToolUseId },
            })
          }
        }
      }
      continue
    }

    if (type === 'user') {
      const message = json.message as Record<string, unknown> | undefined
      if (!message) continue

      const content_items = message.content as Array<Record<string, unknown>> | undefined
      if (!content_items) continue

      const parentToolUseId = json.parent_tool_use_id as string | null

      for (const item of content_items) {
        if (item.type === 'tool_result') {
          const resultContent = item.content as string | Array<Record<string, unknown>>
          let text = ''
          if (typeof resultContent === 'string') {
            text = resultContent
          } else if (Array.isArray(resultContent)) {
            text = resultContent.map(c => (c.text as string) || '').join('\n')
          }

          events.push({
            id: `evt-${eventIndex++}`,
            type: 'tool_result',
            timestamp,
            data: {
              toolUseId: item.tool_use_id as string,
              content: text,
              isError: (item.is_error as boolean) || false,
              parentToolUseId,
            },
          })
        }
      }
      continue
    }

    if (type === 'result') {
      const resultText = (json.result as string) || ''
      events.push({
        id: `evt-${eventIndex++}`,
        type: 'result',
        timestamp,
        data: {
          text: resultText,
          cost: json.cost_usd as number | undefined,
          duration: json.duration_ms as number | undefined,
        },
      })
      continue
    }
  }

  // Post-processing: compute tool call durations from timestamps
  const toolResultTimestamps = new Map<string, number>()
  for (const event of events) {
    if (event.type === 'tool_result') {
      const ts = parseTimestamp(event.timestamp)
      if (ts !== null) {
        toolResultTimestamps.set(event.data.toolUseId, ts)
      }
    }
  }
  for (const event of events) {
    if (event.type === 'tool_call') {
      const callTs = parseTimestamp(event.timestamp)
      const resultTs = toolResultTimestamps.get(event.data.toolUseId)
      if (callTs !== null && resultTs !== undefined) {
        event.data.durationMs = resultTs - callTs
      }
    }
  }

  // Post-processing: deduplicate text that matches the final result
  const resultEvent = events.find(e => e.type === 'result') as ResultEvent | undefined
  if (resultEvent) {
    const resultText = resultEvent.data.text.trim()
    return events.filter(e => {
      if (e.type !== 'text') return true
      // Remove top-level text events that match the result content
      if (!e.data.parentToolUseId && e.data.text.trim() === resultText) return false
      return true
    })
  }

  return events
}

export { getToolInputSummary }
